import { createSignal, onMount, For, Show } from "solid-js";

import { useI18n } from "@uic/adapters/i18n";
import alarmSvg from "@uic/assets/icons/alarm-bold.svg?raw";
import trashSvg from "@uic/assets/icons/trash-bold.svg?raw";
import { isLocalStorage, whenBackendSettled } from "@uic/domain/backend";
import {
  UIAlert,
  UIButton,
  UICheckbox,
  UIIcon,
  UILoading,
  UISelect,
} from "@uic/domain/generics";
import { notify } from "@uic/domain/notifications";

import { DeviceClaimedError, enableThisDevice } from "../core/enable-device";
import {
  deleteSubscription,
  getNotificationSettings,
  isRegistrationConflict,
  sendTestNotification,
  updateNotificationSettings,
  updateSubscription,
} from "../core/push-service";
import {
  currentEndpoint,
  currentTimezone,
  isPushSupported,
  PushPermissionError,
  unsubscribeThisDevice,
} from "../core/subscription";
import { DIGEST_TIME_KEYS } from "../labels";

/**
 * Renders a minute past midnight as a clock time.
 * @param {number} minute Minutes since local midnight
 * @returns {string} The time as HH:mm
 */
function formatLocalMinute(minute) {
  const hours = String(Math.floor(minute / 60)).padStart(2, "0");
  const minutes = String(minute % 60).padStart(2, "0");

  return `${hours}:${minutes}`;
}

/**
 * The message to show for a failed action.
 *
 * A raw error message names a fetch, a status code or a broken call, none of
 * which the reader can act on, so the failure is described by what the user was
 * trying to do. The failures the reader can do something about say so in their
 * own words instead.
 * @param {unknown} error The rejected value
 * @param {string} fallbackKey Translation key describing the failed action
 * @returns {string} The translation key to show
 */
function messageKeyFor(error, fallbackKey) {
  if (error instanceof PushPermissionError) return "domain.push.errors.blocked";
  if (error instanceof DeviceClaimedError) return "domain.push.errors.claimed";
  if (isRegistrationConflict(error)) return "domain.push.errors.stale";

  return fallbackKey;
}

/**
 * Settings panel for the daily agenda digest.
 *
 * The schedule belongs to the account and the timezone belongs to the device, so
 * this device gets its own section and the others are listed below it.
 * @returns {import('solid-js').JSX.Element} The notification settings panel
 */
export function NotificationSettings() {
  const { t } = useI18n();

  const [isLoading, setIsLoading] = createSignal(true);
  const [isBusy, setIsBusy] = createSignal(false);
  const [error, setError] = createSignal("");

  const [digestTime, setDigestTime] = createSignal(null);
  const [digestTimes, setDigestTimes] = createSignal([]);
  const [subscriptions, setSubscriptions] = createSignal([]);
  const [endpoint, setEndpoint] = createSignal(null);
  const [isLocal, setIsLocal] = createSignal(false);
  const [vapidKey, setVapidKey] = createSignal("");

  const thisDevice = () =>
    subscriptions().find((sub) => sub.endpoint === endpoint()) || null;

  const otherDevices = () =>
    subscriptions().filter((sub) => sub.endpoint !== endpoint());

  const selectedOption = () =>
    digestTimes().find((option) => option.time === digestTime()) || null;

  const isConfigured = () => Boolean(vapidKey());

  const isDisabled = () =>
    isLocal() || !isPushSupported() || !isConfigured() || isBusy();

  const optionLabel = (option) =>
    `${t(DIGEST_TIME_KEYS[option.time])} · ${formatLocalMinute(option.localMinute)}`;

  const refresh = async () => {
    const [settings, deviceEndpoint] = await Promise.all([
      getNotificationSettings(),
      currentEndpoint(),
    ]);

    setDigestTime(settings.settings?.digestTime ?? null);
    setDigestTimes(settings.digestTimes || []);
    setSubscriptions(settings.subscriptions || []);
    setEndpoint(deviceEndpoint);
    setVapidKey(settings.vapidPublicKey || "");
  };

  onMount(async () => {
    try {
      await whenBackendSettled();
      setIsLocal(isLocalStorage());

      if (!isLocal()) await refresh();
    } catch (err) {
      console.error("Failed to load notification settings", err);
      setError(t("domain.push.errors.load"));
    } finally {
      setIsLoading(false);
    }
  });

  /**
   * Runs an action that changes stored state, reloading afterwards so the panel
   * shows what the server holds rather than what it hoped for.
   * @param {() => Promise<void>} action The action to run
   * @param {string} failureKey Translation key for the failure message
   * @returns {Promise<void>} Resolves once the action and reload are done
   */
  const run = async (action, failureKey) => {
    setIsBusy(true);
    setError("");
    try {
      await action();
      await refresh();
    } catch (err) {
      console.error(t(failureKey), err);
      setError(t(messageKeyFor(err, failureKey)));
    } finally {
      setIsBusy(false);
    }
  };

  const handleEnable = () => {
    const key = vapidKey();

    return run(async () => {
      await enableThisDevice(key);
      notify.success(t("domain.push.enabled"));
    }, "domain.push.errors.enable");
  };

  const handleDisable = () => {
    const device = thisDevice();

    return run(async () => {
      await unsubscribeThisDevice();
      if (device) await deleteSubscription(device.id);
      setEndpoint(null);
    }, "domain.push.errors.disable");
  };

  const handleToggleDevice = (device, enabled) =>
    run(async () => {
      await updateSubscription(device.id, {
        enabled,
        deviceLabel: device.deviceLabel,
        timezone: device.timezone,
      });
    }, "domain.push.errors.update");

  const handleRemoveDevice = (device) =>
    run(async () => {
      await deleteSubscription(device.id);
    }, "domain.push.errors.remove");

  const handleDigestTime = (option) =>
    run(async () => {
      await updateNotificationSettings(option.time);
      setDigestTime(option.time);
    }, "domain.push.errors.schedule");

  const handleTest = () => {
    const device = thisDevice();

    return run(async () => {
      await sendTestNotification(device.id);
      notify.success(t("domain.push.testSent"));
    }, "domain.push.errors.test");
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-8">
      <div>
        <h2 className="tw:font-small-caps tw:text-section">
          {t("domain.push.heading")}
        </h2>
        <p className="tw:mt-1 tw:text-dense tw:text-base-content/70">
          {t("domain.push.description")}
        </p>
      </div>

      <Show when={!isLoading()} fallback={<UILoading />}>
        <Show when={isLocal()}>
          <UIAlert variant="info">{t("domain.push.localStorage")}</UIAlert>
        </Show>

        <Show when={!isLocal() && !isPushSupported()}>
          <UIAlert variant="warning">{t("domain.push.unsupported")}</UIAlert>
        </Show>

        <Show when={!isLocal() && isPushSupported() && !isConfigured()}>
          <UIAlert variant="warning">{t("domain.push.notConfigured")}</UIAlert>
        </Show>

        <Show when={error()}>
          <UIAlert variant="error">{error()}</UIAlert>
        </Show>

        <Show when={!isLocal()}>
          <div className="tw:flex tw:flex-col tw:gap-3">
            <h3 className="tw:font-small-caps tw:text-section">
              {t("domain.push.thisDevice.heading")}
            </h3>

            <p className="tw:text-meta tw:text-base-content/70">
              {t("domain.push.thisDevice.timezone")}{" "}
              <span className="tw:font-mono">{currentTimezone()}</span>
            </p>

            <Show
              when={thisDevice()}
              fallback={
                <div>
                  <UIButton
                    variant="primary"
                    onClick={handleEnable}
                    disabled={isDisabled()}
                  >
                    <UIIcon svg={alarmSvg} size="sm" />
                    <span>{t("domain.push.thisDevice.enable")}</span>
                  </UIButton>
                </div>
              }
            >
              <div className="tw:flex tw:flex-wrap tw:gap-2">
                <UIButton onClick={handleTest} disabled={isDisabled()}>
                  {t("domain.push.thisDevice.test")}
                </UIButton>
                <UIButton
                  variant="ghost"
                  onClick={handleDisable}
                  disabled={isDisabled()}
                >
                  {t("domain.push.thisDevice.disable")}
                </UIButton>
              </div>
            </Show>
          </div>

          <div className="tw:flex tw:flex-col tw:gap-3 tw:md:max-w-md">
            <h3 className="tw:font-small-caps tw:text-section">
              {t("domain.push.schedule.heading")}
            </h3>
            <p className="tw:text-dense tw:text-base-content/70">
              {t("domain.push.schedule.description")}
            </p>
            <UISelect
              value={selectedOption()}
              options={digestTimes()}
              renderOption={(option) => optionLabel(option)}
              onChange={handleDigestTime}
              disabled={isDisabled()}
            />
          </div>

          <Show when={otherDevices().length > 0}>
            <div className="tw:flex tw:flex-col tw:gap-3">
              <h3 className="tw:font-small-caps tw:text-section">
                {t("domain.push.otherDevices.heading")}
              </h3>
              <p className="tw:text-dense tw:text-base-content/70">
                {t("domain.push.otherDevices.description")}
              </p>

              <ul className="tw:flex tw:flex-col tw:gap-2">
                <For each={otherDevices()}>
                  {(device) => (
                    <li className="tw:flex tw:items-center tw:justify-between tw:gap-4 tw:border tw:border-base-content/20 tw:p-3">
                      <div className="tw:flex tw:flex-col">
                        <span className="tw:text-dense">
                          {device.deviceLabel ||
                            t("domain.push.otherDevices.unnamed")}
                        </span>
                        <span className="tw:font-mono tw:text-meta tw:text-base-content/70">
                          {device.timezone}
                        </span>
                      </div>
                      <div className="tw:flex tw:items-center tw:gap-3">
                        <UICheckbox
                          checked={device.enabled}
                          size="sm"
                          disabled={isBusy()}
                          label={t("domain.push.otherDevices.enabled")}
                          onChange={(enabled) =>
                            handleToggleDevice(device, enabled)
                          }
                        />
                        <UIButton
                          variant="ghost"
                          size="sm"
                          disabled={isBusy()}
                          onClick={() => handleRemoveDevice(device)}
                        >
                          <UIIcon svg={trashSvg} size="sm" />
                        </UIButton>
                      </div>
                    </li>
                  )}
                </For>
              </ul>
            </div>
          </Show>
        </Show>
      </Show>
    </div>
  );
}
